import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { FieldValues, UseFormSetError } from 'react-hook-form';
import { useAuthContext } from '../../auth/AuthContext';
import InformationPageTemplate from '../../components/form/InformationPageTemplate';
import QuestionPageTemplate from '../../components/form/QuestionPageTemplate/QuestionPageTemplate';
import { sortContinuousDynamicTableSubmissionValue } from '../../components/form/DynamicTable';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { useScsQuarterCompletion } from '../../components/scs/useScsQuarterCompletion';
import type { SubsectionRouterProps } from '../../components/Router/SubsectionRouterProps';
import config from '../../config';
import content from '../../content';
import type { Question } from '../../config/types';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { updateForm } from '../../service/form';
import type { FetchFormResponse, UpdateFormParams } from '../../service/types';
import { Query } from '../../types';
import {
  extractConditionalQuestions,
  getEnabledSubsections,
  getLockedSections,
  getLockedSubsections,
  isSubsectionNotRequired,
  isWorkflowOnlyPageName,
} from '../../utils/config-utils';
import { isAdminPreviewMode, withAdminPreviewSearch } from '../../utils/admin-preview';
import { ApiValidationError } from '../../utils/api-error';
import { FormExpiredError } from '../../utils/form-expired-error';
import { resolveSelectedFormId } from '../../utils/form-id';
import { FormSubmittedError } from '../../utils/form-submitted-error';
import { getListAnswers, hasValue } from '../../utils/form-utils';
import Error404Screen from '../ErrorScreen/Error404Screen';
import type { HandleNavigation } from './types';

const { apiUrl, urls } = config;

function Subsection({ subsection, sectionTitle, sectionName, urlPath }: SubsectionRouterProps) {
  const navigate = useNavigate();
  const { page } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { tenant, formConfig, enabledSubsections } = useFormConfig();
  const tenantBasePath = tenant ? `/${tenant}` : '';
  const normalizedUrlPath = urlPath.replace(/^\/+/, '');
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const sectionSlug = normalizedUrlPath.split('/')[0];
  const overviewHash = `#section-${sectionSlug}`;

  const { data: currentData, refetch, isFetching } = useFormData();
  const currentRole = String(currentData?.role || '').toLowerCase();
  const currentFormState = String((currentData as any)?.formState || '').toLowerCase();
  const isPreviewMode = isAdminPreviewMode({
    search: location?.search || '',
    selectedFormId,
    hasAdminAccess: currentData?.hasAdminAccess,
    role: currentData?.role,
    sessionRoles: currentData?.sessionRoles,
    forms: currentData?.forms,
    allowPendingAccessCheck: true,
  });
  const withPreviewSearch = (path: string) => withAdminPreviewSearch(path, isPreviewMode);
  const isReadOnlyViewMode =
    currentRole === 'read_only' || (currentRole === 'approver' && currentFormState === 'active');
  const { fetchJWT } = useAuthContext();
  const [saving, setSaving] = useState(false);
  const isScsStaff = tenant === 'scs' && ['owner', 'editor'].includes(currentRole);
  const scsCompletion = useScsQuarterCompletion({
    enabled: isScsStaff,
    formId: selectedFormId || currentData?.formId || '',
    config: formConfig,
    subsection: subsection.slug,
    getToken: async () => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Authentication required');
      return idToken;
    },
  });

  const { mutateAsync } = useMutation<boolean, Error, UpdateFormParams>({
    throwOnError: false,
    mutationFn: async (params) => {
      const { idToken } = await fetchJWT();
      if (!idToken) return false;
      await updateForm(
        idToken,
        { ...params, ...(isScsStaff && { 'defer-scs-approval': true }) },
        selectedFormId ? { form_id: selectedFormId } : undefined,
      );
      return true;
    },
  });

  const sectionPages = subsection.dynamicPages
    ? enabledSubsections[subsection.slug]?.pages || []
    : (subsection?.pages ?? []);
  const visibleSectionPages = useMemo(
    () => sectionPages.filter(({ name }) => !isWorkflowOnlyPageName(name)),
    [sectionPages],
  );
  const currentPageIndex = useMemo(
    () => (page ? visibleSectionPages?.findIndex(({ name }) => name === page) : 0),
    [page, visibleSectionPages],
  );
  const pageConfig = useMemo(
    () => visibleSectionPages?.[currentPageIndex],
    [currentPageIndex, visibleSectionPages],
  );
  const is404 = useMemo(
    () => !pageConfig || currentPageIndex === -1,
    [currentPageIndex, pageConfig],
  );
  useEffect(() => {
    if (is404 || !isReadOnlyViewMode) return;
    navigate(withPreviewSearch(`${tenantBasePath}/${normalizedUrlPath}/summary`), {
      replace: true,
    });
  }, [is404, isReadOnlyViewMode, navigate, normalizedUrlPath, isPreviewMode, tenantBasePath]);

  const lockedSections = useMemo(
    () => getLockedSections(formConfig.sections, enabledSubsections, currentData),
    [currentData, enabledSubsections, formConfig.sections],
  );
  const lockedSubsections = useMemo(
    () => getLockedSubsections(formConfig.sections, enabledSubsections, currentData),
    [currentData, enabledSubsections, formConfig.sections],
  );
  const isCurrentSubsectionNotRequired = useMemo(
    () => isSubsectionNotRequired(subsection, currentData),
    [currentData, subsection],
  );
  const isCurrentRouteLocked =
    lockedSections[sectionSlug] === true ||
    lockedSubsections[subsection.slug] === true ||
    isCurrentSubsectionNotRequired;

  useEffect(() => {
    if (!isCurrentRouteLocked) return;
    navigate(withPreviewSearch(`${tenantBasePath}${overviewHash}`), { replace: true });
  }, [isCurrentRouteLocked, navigate, overviewHash, isPreviewMode, tenantBasePath]);

  const handleNavigation: HandleNavigation = async () => {
    if (pageConfig?.navigateToSummaryOnComplete) {
      navigate(withPreviewSearch(`${tenantBasePath}${overviewHash}`));
      return;
    }

    const data = (await refetch())?.data;

    const enabledPages = getEnabledSubsections(formConfig, data)[subsection.slug]?.pages ?? [];
    const nextPageIndex = currentPageIndex + 1;
    const enabledVisiblePages = enabledPages.filter(({ name }) => !isWorkflowOnlyPageName(name));
    const isLast = nextPageIndex >= enabledVisiblePages.length;

    // LIST pages: keep existing behaviour
    if (pageConfig?.pageType === 'list') {
      if (!isLast) {
        navigate(
          withPreviewSearch(
            `${tenantBasePath}/${normalizedUrlPath}/${enabledVisiblePages[nextPageIndex].name}`,
          ),
        );
      } else {
        navigate(withPreviewSearch(`${tenantBasePath}/${normalizedUrlPath}/summary`));
      }
      return;
    }

    // OTHER pages: next if not last; else main index
    if (!isLast) {
      navigate(
        withPreviewSearch(
          `${tenantBasePath}/${normalizedUrlPath}/${enabledVisiblePages[nextPageIndex].name}`,
        ),
      );
    } else {
      navigate(withPreviewSearch(`${tenantBasePath}${overviewHash}`));
    }
  };

  // ---------- Helpers to DRY up list completeness + flag sync ----------
  const computeListIncomplete = (
    answers: FetchFormResponse['answers'] | undefined,
    questions: Question[],
  ): boolean => {
    const grouped = getListAnswers(answers || {}, questions);
    const entries = Object.values(grouped) as Array<Array<{ question: Question; answer: unknown }>>;

    // Drop entries where ALL answers are empty / non value-bearing (e.g., after delete blanking)
    const pruned = entries.filter((entry) => entry.some(({ answer }) => hasValue(answer)));

    // No (non-empty) entries => in progress
    if (pruned.length === 0) return true;

    // Incomplete if any required field in any remaining entry is missing a value
    return pruned.some((entry) =>
      entry.some(({ question, answer }) =>
        question?.rules?.required === true ? !hasValue(answer) : false,
      ),
    );
  };

  const handleSave = async (
    formData: FieldValues,
    isDraft: boolean,
    isList = false,
    listItemId?: string,
    setFieldError?: UseFormSetError<FetchFormResponse['answers']>,
    saveMode: 'continue' | 'addAnother' = 'continue',
    stayOnPage = false,
    additionalAnswers: FieldValues = {},
  ): Promise<boolean> => {
    if (!pageConfig) return false;
    formData = (('questions' in pageConfig && pageConfig.questions) || []).reduce(
      (answers, question) =>
        question.dynamicTable?.sortOnSaveBy
          ? {
              ...answers,
              [question.name]: sortContinuousDynamicTableSubmissionValue(
                question.dynamicTable,
                answers[question.name],
              ),
            }
          : answers,
      formData,
    );
    const pageName = pageConfig.name;
    const shouldPersistDraft =
      isDraft &&
      (!isList ||
        !listItemId ||
        Object.entries(formData).some(
          ([fieldName, answer]) => fieldName.startsWith(`${listItemId}-`) && hasValue(answer),
        ));

    console.log('[handleSave] Called with:', {
      pageName,
      isDraft,
      isList,
      listItemId,
    });

    // --- Build local drafts state (remove current page unless this is a draft save) ---
    const drafts = { ...currentData?.drafts };

    // For list items, use "pageName:listItemId" format; otherwise just "pageName"
    const draftIdentifier = listItemId ? `${pageName}:${listItemId}` : pageName;

    // Filter out the current draft identifier
    const filtered = (drafts[subsection.slug] || []).filter((x) => {
      if (listItemId) {
        // For list items, remove only this specific item's draft
        return x !== draftIdentifier;
      }
      // For regular pages, remove the page (and any legacy list item drafts without IDs)
      return x !== pageName && !x.startsWith(`${pageName}:`);
    });

    if (shouldPersistDraft) filtered.push(draftIdentifier);
    drafts[subsection.slug] = filtered;

    // Optimistic cache update so UI feels snappy
    queryClient.setQueryData<FetchFormResponse>([Query.FormData, selectedFormId || ''], {
      ...currentData,
      answers: {
        ...currentData?.answers,
        ...formData,
        ...additionalAnswers,
      },
      drafts,
    });

    if (apiUrl) {
      // Only send the current page's answers
      const questions = extractConditionalQuestions(
        ('questions' in pageConfig && pageConfig.questions) || [],
      );

      const apiData: FetchFormResponse['answers'] = questions.reduce((result, question) => {
        const { name, questionType } = question;
        if (isList) {
          const listAnswers = Object.fromEntries(
            Object.entries(formData).filter(
              ([question, answer]) => question.endsWith(name) && answer !== undefined,
            ),
          );
          return Object.assign(result, listAnswers);
        }

        if (questionType === 'gdadRoleSelectionInput') {
          const groupedAnswers = Object.fromEntries(
            Object.entries(formData).filter(
              ([fieldName, answer]) =>
                (fieldName === name || fieldName.startsWith(`${name}-`)) && answer !== undefined,
            ),
          );
          return Object.assign(result, groupedAnswers);
        }

        let answer = formData?.[name];
        if (typeof answer === 'string') answer = answer.trim();
        return typeof answer !== 'undefined' ? Object.assign(result, { [name]: answer }) : result;
      }, {});
      Object.assign(apiData, additionalAnswers);

      setSaving(true);
      try {
        // 1) Primary save (draft or not)
        const payload = {
          'subsection-id': subsection.slug,
          'page-id': pageName,
          'is-save-unfinished': shouldPersistDraft,
          ...(listItemId && { 'list-item-id': listItemId }),
          answers: apiData,
        };

        console.log('[handleSave] Sending API payload:', payload);

        let ok = false;
        try {
          ok = await mutateAsync(payload);
        } catch (error) {
          if (error instanceof FormExpiredError || error instanceof FormSubmittedError) {
            navigate(`${tenantBasePath}/${config.urls.landingAccounts}`);
            return false;
          }

          if (error instanceof ApiValidationError) {
            const message = String(error.message || '').trim() || content.errorApi;
            // Validation errors (e.g. allowed email domains) are not tied to a single field in the API response.
            // Apply the message to all email fields on the page (unless explicitly opted out).
            const restrictedEmailQuestions = questions.filter(
              (q) => q.rules?.email === true && q.restrictDomains !== false,
            );
            if (typeof setFieldError === 'function' && restrictedEmailQuestions.length) {
              for (const q of restrictedEmailQuestions) {
                setFieldError(q.name, { type: 'manual', message }, { shouldFocus: true });
              }
            }
            return false;
          }

          navigate(`${tenantBasePath}/${urls.defaultError}`);
          return false;
        }

        if (!ok) return false;

        // 2) For list pages, reconcile the save_unfinished flag based on *fresh server state*
        if (isList) {
          // Pull latest answers to avoid cache/merge artefacts after deletes
          const latest = (await refetch())?.data;
          const anyIncomplete = computeListIncomplete(latest?.answers, questions);

          // If it's not a draft save, explicitly sync the flag (draft saves already set true above)
          if (!isDraft) {
            await mutateAsync({
              'subsection-id': subsection.slug,
              'page-id': pageName,
              'is-save-unfinished': anyIncomplete,
              // minimal payload so the lambda accepts the call
              answers: { [pageName]: [] },
            });
          }

          // Refresh once and navigate to index (existing behaviour)
          await queryClient.invalidateQueries({ queryKey: [Query.FormData, selectedFormId || ''] });
          const saved = (await refetch())?.data;
          if (isScsStaff && !isDraft && saveMode === 'continue') {
            const nextPath = await scsCompletion.afterSave(saved);
            if (nextPath) {
              navigate(nextPath);
              return true;
            }
            await refetch();
          }
          if (isDraft) {
            if (stayOnPage) return true;
            navigate(`${tenantBasePath}${overviewHash}`); // draft save stays on main index
          } else if (saveMode === 'addAnother') {
            // Stay on list question page so the client can start a new list entry.
            return true;
          } else {
            navigate(`${tenantBasePath}/${normalizedUrlPath}/summary`); // go to summary/list view after save & continue
          }
          return true;
        }

        // 3) Non-list pages
        if (isDraft) {
          // Draft saves always go back to main index
          await queryClient.invalidateQueries({ queryKey: [Query.FormData, selectedFormId || ''] });
          await refetch();
          if (stayOnPage) return true;
          navigate(withPreviewSearch(`${tenantBasePath}${overviewHash}`));
          return true;
        }

        // Non-draft, non-list: ensure server state fresh, then follow normal nav
        queryClient.setQueryData<FetchFormResponse>(
          [Query.FormData, selectedFormId || ''],
          (prev) => {
            if (!prev) return prev as any;
            const prevDrafts = { ...(prev.drafts || {}) };
            prevDrafts[subsection.slug] = (prevDrafts[subsection.slug] || []).filter(
              (x) => x !== pageName,
            );
            return { ...prev, drafts: prevDrafts };
          },
        );
        await queryClient.invalidateQueries({ queryKey: [Query.FormData, selectedFormId || ''] });
        const saved = (await refetch())?.data;
        const nextPath = await scsCompletion.afterSave(saved);
        if (nextPath) {
          navigate(nextPath);
          return true;
        }
        if (isScsStaff) await refetch();
        await handleNavigation();
        return true;
      } finally {
        setSaving(false);
      }
    }

    return true;
  };

  return is404 ? (
    <Error404Screen />
  ) : (
    <PageTemplate
      key={pageConfig.name}
      breadcrumbs={{
        sectionName,
        sectionTitle: pageConfig.name.startsWith('skill-') ? pageConfig.title : sectionTitle,
      }}
      title={pageConfig.customTitle || pageConfig.title}
      titleClassName={
        pageConfig.titleClassName ||
        (pageConfig.name.startsWith('skill-') ? 'gdad-skill-page-title' : undefined)
      }
      subTitle={pageConfig.hidePageTitle || pageConfig.hideSubTitle ? undefined : sectionTitle}
      limitWidth={pageConfig.pageType === 'information' ? true : pageConfig.limitWidth}
      fullWidth={
        (pageConfig.pageType === 'list' || pageConfig.pageType === 'question') &&
        pageConfig.questions?.some(
          (question) => question.questionType === 'exceptionBreakdownInput',
        )
      }
    >
      {scsCompletion.modal}
      {pageConfig.pageType === 'question' && (
        <QuestionPageTemplate
          config={pageConfig}
          onSave={async (data, setError, listItemId, options) => {
            return await handleSave(
              data,
              false,
              false,
              listItemId,
              setError,
              options?.mode || 'continue',
              false,
              options?.additionalAnswers,
            );
          }}
          onSaveUnfinished={async (data, listItemId, options) => {
            await handleSave(
              data,
              true,
              false,
              listItemId,
              undefined,
              'continue',
              options?.stayOnPage,
            );
          }}
          disabled={saving || isFetching}
          subsectionSlug={subsection.slug}
        />
      )}

      {pageConfig.pageType === 'list' && (
        <QuestionPageTemplate
          config={pageConfig}
          onSave={async (data, setError, listItemId, options) => {
            return await handleSave(
              data,
              false,
              true,
              listItemId,
              setError,
              options?.mode || 'continue',
              false,
              options?.additionalAnswers,
            );
          }}
          onSaveUnfinished={async (data, listItemId, options) => {
            await handleSave(
              data,
              true,
              true,
              listItemId,
              undefined,
              'continue',
              options?.stayOnPage,
            );
          }}
          onContinue={() => {
            navigate(withPreviewSearch(`${tenantBasePath}${overviewHash}`)); // main index
          }}
          disabled={saving || isFetching}
          subsectionSlug={subsection.slug}
        />
      )}

      {pageConfig.pageType === 'information' && (
        <InformationPageTemplate
          config={pageConfig}
          onContinue={handleNavigation}
          disabled={saving || isFetching}
        />
      )}
    </PageTemplate>
  );
}

export default Subsection;
